import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

// Helper function for currency formatting
const formatCurrency = (value, decimals = 0) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    }).format(value);
};

// OutputCell component used by InvestmentFinancingTab
const OutputCell = ({ label, value, unit, formatter = (v) => formatCurrency(v) }) => (
    <div className="grid grid-cols-2 gap-4 items-center py-2 px-3">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
        <div className="flex justify-end items-center space-x-2">
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 bg-gray-100 dark:bg-gray-700 px-3 py-1.5 rounded-md w-full text-right">{formatter(value)}</span>
            {unit && <span className="text-sm text-gray-500 dark:text-gray-400 w-24">{unit}</span>}
        </div>
    </div>
);

// Explanation component used by InvestmentFinancingTab
const Explanation = ({ title, children }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    
    return (
        <div className="mt-8 border-t-2 border-gray-200 dark:border-gray-700 explanation-section">
            <button 
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
            >
                <h4 className="text-lg font-bold text-gray-800 dark:text-gray-200">{title}</h4>
                {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                ) : (
                    <ChevronDown className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                )}
            </button>
            {isExpanded && (
                <div className="px-4 pb-4 text-sm text-gray-600 dark:text-gray-400 space-y-4 animate-fade-in">
                    {children}
                </div>
            )}
        </div>
    );
};

// Main InvestmentFinancingTab component
const InvestmentFinancingTab = ({ outputs }) => {
    const planData = outputs.investmentPlan ? [
        { item: 'Solar Investment', value: outputs.investmentPlan.solarInvestment}, 
        ...(outputs.investmentPlan.bessInvestment > 0 ? [{ item: 'BESS Investment', value: outputs.investmentPlan.bessInvestment}] : []),
        { item: 'Total Static Investment', value: outputs.investmentPlan.totalStaticInvestment}, 
        { item: 'Interest During Construction', value: outputs.investmentPlan.interestDuringConstruction }, 
        { item: 'Working Capital', value: outputs.investmentPlan.workingCapital }, 
        { item: 'Total Investment', value: outputs.investmentPlan.totalInvestment}
    ] : [];
    const financingData = outputs.financing ? [{ item: 'Capital (Own Funds)', value: outputs.financing.capital }, { item: 'Loan', value: outputs.financing.loan }, { item: 'Total Financing', value: outputs.financing.totalFinancing}] : [];
    return (<div><h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">Investment Plan & Financing</h2><div className="grid md:grid-cols-2 gap-8"><div><h3 className="text-lg font-semibold mb-2">Investment Plan</h3><div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">{planData.map(d => <OutputCell key={d.item} label={d.item} value={d.value} />)}</div></div><div><h3 className="text-lg font-semibold mb-2">Financing Sources</h3><div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">{financingData.map(d => <OutputCell key={d.item} label={d.item} value={d.value} />)}</div></div></div>
        <Explanation title="How This Works">
            <p>This tab breaks down the total cost of the project (Uses of Funds) and how it will be paid for (Sources of Funds).</p>
            <p><strong>Principle:</strong> The fundamental accounting equation is at play here: Assets = Liabilities + Equity. In this context, the Total Investment (the assets being created) must equal the Total Financing (the liabilities/loan + equity/own funds used to pay for them).</p>
            <p><strong>Goal:</strong> To clearly show the total capital required for the project and the mix of debt and equity that will be used. This is critical for understanding the project's capital structure and leverage.</p>
        </Explanation>
    </div>);
};

export default InvestmentFinancingTab; 